import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { exercisesQueryOptions } from '@/screens/exercises/exercises-query';
import { foodsQueryOptions } from '@/screens/foods/foods-query';

export interface CatalogPickDialogProps {
  readonly kind: 'food' | 'exercise';
  readonly title: string;
  readonly body: ReactNode;
  readonly initialTerm: string;
  readonly confirmLabel: string;
  readonly isPending: boolean;
  readonly error: string | undefined;
  readonly onConfirm: (catalogId: string) => void;
  readonly onCancel: () => void;
}

/**
 * Pick the catalog row a group of gym items is the same as. The catalog list
 * endpoints answer the search, so this needs no API of its own; searching by
 * name rather than pasting an id is the point (the food requests' resolve
 * picker says why).
 */
export function CatalogPickDialog({
  kind,
  title,
  body,
  initialTerm,
  confirmLabel,
  isPending,
  error,
  onConfirm,
  onCancel,
}: CatalogPickDialogProps) {
  const { api } = useAuth();
  const fieldId = useId();
  const [term, setTerm] = useState(initialTerm);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const debounced = useDebounced(term, 260);
  const enough = debounced.length >= 2;

  const foods = useQuery({
    ...foodsQueryOptions(api, { search: debounced, activity: 'active' }),
    enabled: kind === 'food' && enough,
  });
  const exercises = useQuery({
    ...exercisesQueryOptions(api, { search: debounced, activity: 'active' }),
    enabled: kind === 'exercise' && enough,
  });
  const results = kind === 'food' ? foods : exercises;
  // The catalog list carries gym rows too; only a catalog row can stand for a group.
  const options = (results.data?.items ?? [])
    .filter((item) => !('source' in item) || item.source !== 'gym')
    .map((item) => ({ id: item.id, name: item.name }));

  return (
    <ConfirmDialog
      title={title}
      body={
        <div className="flex flex-col gap-4">
          {body}
          <div className="flex flex-col gap-2">
            <Label htmlFor={fieldId} className="text-xs font-medium text-muted-foreground">
              Search the catalog
            </Label>
            <Input
              id={fieldId}
              type="search"
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setPicked(null);
              }}
            />
          </div>
          {!enough ? (
            <p className="text-[13px] text-muted-foreground">Type at least two letters.</p>
          ) : results.isPending ? (
            <p className="text-[13px] text-muted-foreground">Searching…</p>
          ) : options.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              Nothing active matches that. If the catalog does not have it, cancel and make a catalog {kind}.
            </p>
          ) : (
            <ul className="max-h-[220px] overflow-y-auto rounded-md border border-border">
              {options.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    aria-pressed={picked?.id === option.id}
                    onClick={() => {
                      setPicked(option);
                    }}
                    className={cn(
                      'flex w-full px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
                      picked?.id === option.id ? 'bg-primary/10 font-medium text-primary' : 'text-foreground',
                    )}
                  >
                    {option.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      }
      confirmLabel={confirmLabel}
      confirmDisabled={picked === null}
      onConfirm={() => {
        if (picked !== null) onConfirm(picked.id);
      }}
      onCancel={onCancel}
      isPending={isPending}
      error={error}
    />
  );
}
